/**
 * wallSprite.ts — richer wall-player rendering, factored out of render.ts.
 *
 * Inputs are pre-projected screen points + the screen-pixel scale derived
 * from `(playerWidthMeters * focal) / wallDistanceMeters`. The sprite
 * file does NO projection itself — that stays in render.ts so physics
 * anchors (head/shoulder/feet) remain the single source of truth.
 *
 * Visual upgrade vs. v1 silhouette:
 *   - Per-player jersey colour with a faint horizontal accent band
 *   - Subtle highlight on torso to suggest volume
 *   - Detached head (skin tone) with hair-line shading
 *   - Hands clasped in front of groin (the classic free-kick wall pose)
 *   - Soft drop-shadow on the grass at each player's feet
 *
 * Anchors are unchanged so physics stays in sync. The drawn body fits
 * inside the same (feet → shoulder → head) envelope.
 */

interface ScreenPoint {
  x: number;
  y: number;
  visible: boolean;
}

export interface WallPlayerOpts {
  feet: ScreenPoint;
  shoulder: ScreenPoint;
  head: ScreenPoint;
  /** Screen-pixel torso width (already perspective-scaled by caller). */
  torsoPx: number;
  /** Screen-pixel head radius (already perspective-scaled by caller). */
  headPx: number;
  /** Player index in the wall — used to pick a deterministic skin/hair shade
   *  so the wall doesn't look like a row of clones. */
  index: number;
}

/** Deterministic colour pick per index so the wall has subtle variation
 *  without flickering frame-to-frame. */
function variantFor(index: number): {
  skin: string;
  hair: string;
  jersey: string;
  jerseyShadow: string;
  shorts: string;
} {
  const variants = [
    {
      skin: "#d9a883",
      hair: "#1a1410",
      jersey: "#1e293b",
      jerseyShadow: "#0f172a",
      shorts: "#0b1220",
    },
    {
      skin: "#b07453",
      hair: "#0f0a08",
      jersey: "#1e293b",
      jerseyShadow: "#0f172a",
      shorts: "#0b1220",
    },
    {
      skin: "#e8c39d",
      hair: "#3b2515",
      jersey: "#1e293b",
      jerseyShadow: "#0f172a",
      shorts: "#0b1220",
    },
    {
      skin: "#8e5a3c",
      hair: "#000000",
      jersey: "#1e293b",
      jerseyShadow: "#0f172a",
      shorts: "#0b1220",
    },
  ];
  return variants[index % variants.length]!;
}

/** Draw one wall player. Caller is responsible for visibility checks. */
export function drawWallPlayer(
  ctx: CanvasRenderingContext2D,
  opts: WallPlayerOpts,
): void {
  const { feet, shoulder, head, torsoPx, headPx, index } = opts;
  const v = variantFor(index);

  // Soft grass shadow under the feet — sells weight on the pitch.
  ctx.fillStyle = "rgba(0,0,0,0.32)";
  ctx.beginPath();
  ctx.ellipse(feet.x, feet.y + 1, torsoPx * 0.55, torsoPx * 0.14, 0, 0, Math.PI * 2);
  ctx.fill();

  // Legs — three sections per leg so wall players read as real humans, not
  // shorts-from-crotch-to-toe:
  //   1. Shorts (top of leg, jersey-shorts colour)
  //   2. Bare calf (skin tone)
  //   3. Football boot (cleat, distinct dark colour)
  // Boundaries:
  //   crotchY      — top of legs
  //   shortsBottom — ~45% down from crotch to feet (mid-thigh)
  //   ankleY       — ~12% above feet.y (top of boot)
  //   feet.y       — ground line (bottom of boot)
  const crotchY = feet.y - (feet.y - shoulder.y) * 0.42;
  const crotchOffset = torsoPx * 0.06;
  const legSpan = feet.y - crotchY;
  const shortsBottomY = crotchY + legSpan * 0.45;
  const ankleY = feet.y - legSpan * 0.12;

  // Geometry helper — each leg has 4 horizontal "rungs" we slice between.
  // Left leg outer/inner x at each y-level; right leg mirrors it.
  const legOuterCrotch = torsoPx * 0.34;
  const legInnerCrotch = crotchOffset;
  const legOuterFeet   = torsoPx * 0.20;
  const legInnerFeet   = torsoPx * 0.05;

  // Linear interpolate x positions at any y between crotchY and feet.y.
  const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;
  const tShortsBottom = (shortsBottomY - crotchY) / legSpan;
  const tAnkle        = (ankleY        - crotchY) / legSpan;
  const outerAtShortsBottom = lerp(legOuterCrotch, legOuterFeet, tShortsBottom);
  const innerAtShortsBottom = lerp(legInnerCrotch, legInnerFeet, tShortsBottom);
  const outerAtAnkle        = lerp(legOuterCrotch, legOuterFeet, tAnkle);
  const innerAtAnkle        = lerp(legInnerCrotch, legInnerFeet, tAnkle);

  // Shorts (top section, both legs as one panel for clean silhouette).
  ctx.fillStyle = v.shorts;
  ctx.beginPath();
  ctx.moveTo(feet.x - legOuterCrotch, crotchY);
  ctx.lineTo(feet.x - outerAtShortsBottom, shortsBottomY);
  ctx.lineTo(feet.x - innerAtShortsBottom, shortsBottomY);
  ctx.lineTo(feet.x - legInnerCrotch, crotchY);
  ctx.lineTo(feet.x + legInnerCrotch, crotchY);
  ctx.lineTo(feet.x + innerAtShortsBottom, shortsBottomY);
  ctx.lineTo(feet.x + outerAtShortsBottom, shortsBottomY);
  ctx.lineTo(feet.x + legOuterCrotch, crotchY);
  ctx.closePath();
  ctx.fill();

  // Calves — skin tone, one trapezoid per leg.
  ctx.fillStyle = v.skin;
  // Left calf.
  ctx.beginPath();
  ctx.moveTo(feet.x - outerAtShortsBottom, shortsBottomY);
  ctx.lineTo(feet.x - outerAtAnkle, ankleY);
  ctx.lineTo(feet.x - innerAtAnkle, ankleY);
  ctx.lineTo(feet.x - innerAtShortsBottom, shortsBottomY);
  ctx.closePath();
  ctx.fill();
  // Right calf.
  ctx.beginPath();
  ctx.moveTo(feet.x + innerAtShortsBottom, shortsBottomY);
  ctx.lineTo(feet.x + innerAtAnkle, ankleY);
  ctx.lineTo(feet.x + outerAtAnkle, ankleY);
  ctx.lineTo(feet.x + outerAtShortsBottom, shortsBottomY);
  ctx.closePath();
  ctx.fill();

  // Football boots — flat cleats sitting on the pitch. Slightly wider than
  // the ankle so they read as boots, not just feet. Dark colour distinct
  // from the shorts so the wall players have a clear "boot line".
  const bootOverhang = torsoPx * 0.06;
  ctx.fillStyle = "#0a0a0a";
  // Left boot.
  ctx.beginPath();
  ctx.moveTo(feet.x - outerAtAnkle - bootOverhang * 0.3, ankleY);
  ctx.lineTo(feet.x - legOuterFeet - bootOverhang,       feet.y);
  ctx.lineTo(feet.x - legInnerFeet + bootOverhang * 0.4, feet.y);
  ctx.lineTo(feet.x - innerAtAnkle + bootOverhang * 0.2, ankleY);
  ctx.closePath();
  ctx.fill();
  // Right boot.
  ctx.beginPath();
  ctx.moveTo(feet.x + innerAtAnkle - bootOverhang * 0.2, ankleY);
  ctx.lineTo(feet.x + legInnerFeet - bootOverhang * 0.4, feet.y);
  ctx.lineTo(feet.x + legOuterFeet + bootOverhang,       feet.y);
  ctx.lineTo(feet.x + outerAtAnkle + bootOverhang * 0.3, ankleY);
  ctx.closePath();
  ctx.fill();
  // Thin highlight stripe along the boot top — sells "football boot" vs
  // "black blob" at small sizes. Three-stripes-of-Adidas vibe without
  // committing to a brand.
  ctx.strokeStyle = "rgba(255,255,255,0.55)";
  ctx.lineWidth = Math.max(1, torsoPx * 0.03);
  ctx.beginPath();
  ctx.moveTo(feet.x - outerAtAnkle - bootOverhang * 0.2, ankleY + Math.max(1, torsoPx * 0.03));
  ctx.lineTo(feet.x - innerAtAnkle + bootOverhang * 0.1, ankleY + Math.max(1, torsoPx * 0.03));
  ctx.moveTo(feet.x + innerAtAnkle - bootOverhang * 0.1, ankleY + Math.max(1, torsoPx * 0.03));
  ctx.lineTo(feet.x + outerAtAnkle + bootOverhang * 0.2, ankleY + Math.max(1, torsoPx * 0.03));
  ctx.stroke();

  // Torso — trapezoid, jersey colour, with a faint shadow side for depth.
  const torsoTopHalf = torsoPx * 0.44;
  const torsoBotHalf = torsoPx * 0.34;
  ctx.fillStyle = v.jersey;
  ctx.beginPath();
  ctx.moveTo(crotchOffset + shoulder.x - torsoBotHalf, crotchY);
  ctx.lineTo(shoulder.x - torsoTopHalf, shoulder.y);
  ctx.lineTo(shoulder.x + torsoTopHalf, shoulder.y);
  ctx.lineTo(crotchOffset + shoulder.x + torsoBotHalf - crotchOffset * 2, crotchY);
  ctx.closePath();
  ctx.fill();

  // Right-side jersey shadow for volume (light from upper-left).
  ctx.fillStyle = v.jerseyShadow;
  ctx.beginPath();
  ctx.moveTo(shoulder.x + torsoTopHalf * 0.45, shoulder.y);
  ctx.lineTo(shoulder.x + torsoTopHalf, shoulder.y);
  ctx.lineTo(shoulder.x + torsoBotHalf, crotchY);
  ctx.lineTo(shoulder.x + torsoBotHalf * 0.55, crotchY);
  ctx.closePath();
  ctx.fill();

  // Accent stripe across the chest — gives an obvious "team kit" feeling
  // without needing a real jersey texture.
  const stripeY = shoulder.y + (crotchY - shoulder.y) * 0.32;
  const stripeH = Math.max(1.5, torsoPx * 0.08);
  ctx.fillStyle = "rgba(255,255,255,0.62)";
  ctx.fillRect(
    shoulder.x - torsoTopHalf * 0.92,
    stripeY,
    torsoTopHalf * 1.84,
    stripeH,
  );

  // Arms — short stubby sleeves leading to forearms that meet in front of
  // the groin (free-kick wall pose). Forearm tone uses skin so the clasped
  // hands read clearly even at small sizes.
  const sleeveLen = torsoPx * 0.22;
  const sleeveW = torsoPx * 0.20;
  ctx.fillStyle = v.jersey;
  // Left sleeve.
  ctx.fillRect(shoulder.x - torsoTopHalf - sleeveW * 0.4, shoulder.y, sleeveW, sleeveLen);
  // Right sleeve.
  ctx.fillRect(shoulder.x + torsoTopHalf - sleeveW * 0.6, shoulder.y, sleeveW, sleeveLen);

  // Forearms reaching down + inward to the groin guard pose.
  ctx.fillStyle = v.skin;
  const handY = crotchY + torsoPx * 0.02;
  const handCx = shoulder.x;
  const handHalfW = torsoPx * 0.20;
  // Forearm strokes.
  ctx.lineWidth = Math.max(1.6, sleeveW * 0.7);
  ctx.lineCap = "round";
  ctx.strokeStyle = v.skin;
  ctx.beginPath();
  ctx.moveTo(shoulder.x - torsoTopHalf, shoulder.y + sleeveLen);
  ctx.lineTo(handCx - handHalfW * 0.4, handY);
  ctx.moveTo(shoulder.x + torsoTopHalf, shoulder.y + sleeveLen);
  ctx.lineTo(handCx + handHalfW * 0.4, handY);
  ctx.stroke();
  // Clasped hands — single wide ellipse over the crotch.
  ctx.fillStyle = v.skin;
  ctx.beginPath();
  ctx.ellipse(handCx, handY, handHalfW, torsoPx * 0.12, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = "rgba(0,0,0,0.45)";
  ctx.lineWidth = 1;
  ctx.stroke();

  // Neck — short stub between shoulder line and head.
  ctx.fillStyle = v.skin;
  ctx.fillRect(
    head.x - headPx * 0.32,
    shoulder.y - headPx * 0.35,
    headPx * 0.64,
    headPx * 0.45,
  );

  // Head — skin-tone circle with a hair cap on top.
  ctx.fillStyle = v.skin;
  ctx.beginPath();
  ctx.arc(head.x, head.y, headPx, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = "rgba(0,0,0,0.5)";
  ctx.lineWidth = 1;
  ctx.stroke();

  // Hair — top half of head, leaving a forehead line visible.
  ctx.fillStyle = v.hair;
  ctx.beginPath();
  ctx.arc(head.x, head.y, headPx, Math.PI * 1.10, Math.PI * 1.90, false);
  ctx.lineTo(head.x + headPx * 0.95, head.y - headPx * 0.05);
  ctx.lineTo(head.x - headPx * 0.95, head.y - headPx * 0.05);
  ctx.closePath();
  ctx.fill();

  // Small highlight dot on the cheek to suggest a face without drawing
  // features that would jitter at distance.
  ctx.fillStyle = "rgba(255,255,255,0.18)";
  ctx.beginPath();
  ctx.arc(head.x - headPx * 0.18, head.y + headPx * 0.18, headPx * 0.22, 0, Math.PI * 2);
  ctx.fill();
}
